import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createLead } from '../../store/slices/leadSlice';
import { LEAD_STATUS } from '../../utils/constants';
import api from '../../services/api';

const LeadModal = ({ onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [salesEmployees, setSalesEmployees] = useState([]);

  const [formData, setFormData] = useState({
    assigned_to: user?._id ? [user._id] : [],
    name: '',
    phone: '',
    email: '',
    company: '',
    source: '',
    status: LEAD_STATUS.NEW
  });

  useEffect(() => {
    // Fetch sales employees for assignment
    const fetchSalesEmployees = async () => {
      try {
        const response = await api.get('/employees/by-department?department=sales');
        setSalesEmployees(response.data.data);
      } catch (err) {
        console.error('Failed to fetch sales employees:', err);
        // Fallback to current user if fetch fails
        setSalesEmployees([{ _id: user._id, name: user.name }]);
      }
    };
    fetchSalesEmployees();
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'assigned_to') {
      // Handle multi-select for assigned employees
      const options = Array.from(e.target.selectedOptions, option => option.value);
      setFormData({
        ...formData,
        assigned_to: options
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await dispatch(createLead(formData)).unwrap();
      onSuccess();
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Create New Lead</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="alert alert-danger">{error}</div>}

                <div className="mb-3">
                  <label className="form-label">Assigned To * (Hold Ctrl/Cmd to select multiple)</label>
                  <select
                    name="assigned_to"
                    className="form-select"
                    value={formData.assigned_to}
                    onChange={handleChange}
                    multiple
                    required
                    style={{ minHeight: '120px' }}
                  >
                    {salesEmployees.map((emp) => (
                      <option key={emp._id} value={emp._id}>
                        {emp.name} ({emp.employee_code})
                      </option>
                    ))}
                  </select>
                  <small className="text-muted d-block mt-1">
                    Selected: {formData.assigned_to.length > 0
                      ? salesEmployees.filter(emp => formData.assigned_to.includes(emp._id)).map(emp => emp.name).join(', ')
                      : 'None'}
                  </small>
                </div>

                <div className="mb-3">
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    className="form-control"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Company *</label>
                  <input
                    type="text"
                    name="company"
                    className="form-control"
                    value={formData.company}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Source</label>
                  <input
                    type="text"
                    name="source"
                    className="form-control"
                    value={formData.source}
                    onChange={handleChange}
                    placeholder="e.g., Website, Referral, Cold Call"
                  />
                </div>

                <div className="mb-0">
                  <label className="form-label">Status *</label>
                  <select
                    name="status"
                    className="form-select"
                    value={formData.status}
                    onChange={handleChange}
                    required
                  >
                    {Object.values(LEAD_STATUS).map((status) => (
                      <option key={status} value={status}>
                        {status.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Lead'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default LeadModal;
